import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { testimonialList } from "../../constants/data";
import { shapeOne, shapeTwo } from "../../constants/svgIcons";
import { useWindowSize } from "../../hooks/useWindowSize"; // You'll need to create this hook

const Testimonial = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for next, -1 for prev
  const [testimonialsPerPage, setTestimonialsPerPage] = useState(1);
  const wrapperRef = useRef();
  const { t } = useTranslation();
  const windowSize = useWindowSize();

  // Calculate testimonials per page based on window width
  useEffect(() => {
    // if (windowSize.width < 700) {
      setTestimonialsPerPage(1);
    // } else if (windowSize.width >= 700 && windowSize.width < 1500) {
    //   setTestimonialsPerPage(2);
    // } else {
    //   setTestimonialsPerPage(3);
    // }
    // Reset to first page when layout changes
    setCurrentIndex(0);
  }, [windowSize.width]);

  const totalPages = Math.ceil(testimonialList.length / testimonialsPerPage);

  const handleChangePage = (newIndex) => {
    if (newIndex === currentIndex) return;
    setDirection(newIndex > currentIndex ? 1 : -1);
    setCurrentIndex((newIndex + totalPages) % totalPages);
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const tl = gsap.timeline();

    tl.fromTo(
      el,
      { x: direction * 100, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.5, ease: "power2.out" }
    );

    return () => {
      if (el) gsap.set(el, { clearProps: "all" });
    };
  }, [currentIndex, testimonialsPerPage]);

  // Get current testimonials to display
  const getCurrentTestimonials = () => {
    const startIndex = currentIndex * testimonialsPerPage;
    return testimonialList.slice(startIndex, startIndex + testimonialsPerPage);
  };

  // Determine grid columns based on testimonials per page
  const getGridColumnsClass = () => {
    switch(testimonialsPerPage) {
      case 1: return "grid-cols-1";
      case 2: return "grid-cols-1 md:grid-cols-2";
      case 3: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      default: return "grid-cols-1";
    }
  };

  return (
    <section className="relative py-20 overflow-hidden">
      {shapeOne}
      {shapeTwo}
      <div className="md:w-[70%] w-mobile-container mx-auto px-4 text-center">
        <h2 className="text-3xl text-gray-800 mb-4">
          {/*{t('common.thisWord')} <br />*/}
          <span className="text-blue-600"> {t('common.whatClientsSay')} .</span>
        </h2>

        <div className="mt-12 relative flex flex-col justify-between md:h-[450px] ">
          {/* Controls - Only show if there's more than one page */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => handleChangePage(currentIndex - 1)}
                className="text-blue-600 hover:text-blue-800 transition px-4 py-2 rounded-full bg-white border shadow hover:shadow-lg"
              >
                ‹
              </button>
              <button
                onClick={() => handleChangePage(currentIndex + 1)}
                className="text-blue-600 hover:text-blue-800 transition px-4 py-2 rounded-full bg-white shadow border hover:shadow-lg"
              >
                ›
              </button>
            </div>
          )}

          {/* Animated container */}
          <div
            ref={wrapperRef}
            className={`grid ${getGridColumnsClass()} gap-6`}
          >
            {getCurrentTestimonials().map((testimonial, i) => (
              <div
                key={i}
                className="bg-white p-6 overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all "
              >
                <div className="flex items-center mb-4 gap-5">
                  <img
                    src={testimonial.img?.startsWith('http')
                      ? testimonial.img
                      : require(`../../assets/images/testimonials/${testimonial.img}`)}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="text-left">
                    <h5 className="font-semibold text-gray-900">{testimonial.name}</h5>
                    <p className="text-sm text-gray-500">{testimonial.position}</p>
                  </div>
                </div>
                <div className="h-[350px] md:h-[300px] lg:h-[280px] flex justify-center items-center">
                  <h1 className="2xl:text-2xl text-gray-700">
                    <div className="text-start !text-7xl">“</div>
                    {t(`testimonialSection.${testimonial.translateKeys.content}`)}
                    <div className="text-end !text-7xl">”</div>
                  </h1>
                </div>
              </div>
            ))}
          </div>

          {/* Dots - Only show if there's more than one page */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center items-end gap-2">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleChangePage(idx)}
                  className={`w-3 h-3 rounded-full transition ${
                    idx === currentIndex ? "bg-blue-600" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Testimonial;